import { usePublicFundreadySubmitCreate } from "@boundless/bl-api-client/public/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormProvider, useForm } from "react-hook-form"
import { z } from "zod"

import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormConsentText } from "@/components/form-elements/FormConsentText"
import { FormInput } from "@/components/form-elements/FormInput"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import {
  fmt$,
  GATE_DISCLOSURE,
  gateCopy,
  gatePreviewRows,
} from "@/lib/fundready/report-content"

import type { FundReadyReport } from "@boundless/bl-api-client/public/models/FundReadyReport"
import type { ProductLenderCount } from "@boundless/bl-api-client/public/models/ProductLenderCount"

const leadEmailSchema = z.object({
  email: z
    .string()
    .min(1, "Enter your business email")
    .email("Enter a valid email"),
  // Declares the field for the form; the disabled submit button is what
  // actually enforces acceptance.
  hasAcceptedTerms: z.boolean(),
})

type LeadEmailValues = z.infer<typeof leadEmailSchema>

type GateModalProps = {
  leadUuid: string
  report: FundReadyReport
  lenderCounts: ProductLenderCount[]
  onSuccess: () => void
  onBack: () => void
}

export function GateModal({
  leadUuid,
  report,
  lenderCounts,
  onSuccess,
  onBack,
}: GateModalProps) {
  const form = useForm<LeadEmailValues>({
    resolver: zodResolver(leadEmailSchema),
    defaultValues: { email: "", hasAcceptedTerms: false },
  })
  const hasAcceptedTerms = form.watch("hasAcceptedTerms")
  // The backend rejects generic/disposable domains with a field-level message
  // ("Please use your company email address…"); surface it under the input
  // instead of the generic failure line.
  const submitMutation = usePublicFundreadySubmitCreate({
    mutation: {
      onSuccess,
      onError: (error) => {
        if (error.status === 400) {
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        }
      },
    },
  })

  const totalLenders = lenderCounts.reduce((sum, l) => sum + l.count, 0)
  const gate = gateCopy(totalLenders)

  function submitLead({ email }: LeadEmailValues) {
    submitMutation.mutate({ data: { leadUuid, email } })
  }

  return (
    <Dialog open>
      <DialogContent
        allowClosing={false}
        // The report is what's being gated, so an email is the only way past:
        // no close button, no Esc, no click-outside.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        // Dialog defaults to rounded-lg; every card on this surface is
        // rounded-xl, and this one sits directly on top of one.
        className="rounded-xl"
      >
        <DialogTitle className="text-fr-brand-strong text-[19px] font-extrabold">
          {gate.headline}
        </DialogTitle>
        <DialogDescription className="text-fr-muted text-sm leading-[1.5]">
          {gate.subcopy}
        </DialogDescription>

        {gate.showPreview && (
          <div
            aria-hidden="true"
            className="pointer-events-none text-sm leading-loose blur-[5px] select-none"
          >
            {gatePreviewRows(report.rangeLow, report.rangeHigh).map((row) => (
              <div key={row.lender}>
                ▸ {row.lender} — {fmt$(row.amount)} · {row.terms}
              </div>
            ))}
          </div>
        )}

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(submitLead)}>
            <FormInput<LeadEmailValues>
              name="email"
              type="email"
              placeholder="you@business.com"
              aria-label="Business email"
            />
            <p className="text-fr-muted mt-3 text-[12.5px] leading-[1.45]">
              {GATE_DISCLOSURE}
            </p>
            <div className="mt-3 flex justify-center">
              <FormCheckbox<LeadEmailValues>
                name="hasAcceptedTerms"
                className="size-6 self-center"
                messageClassName="text-center"
                label={
                  <FormConsentText
                    action="checking this box"
                    className="max-w-[21rem]"
                  />
                }
              />
            </div>
            <div className="mt-4 flex justify-between gap-2.5">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={submitMutation.isPending}
              >
                Back
              </Button>
              {submitMutation.isPending ? (
                <LoadingButton type="button">
                  Opening your report…
                </LoadingButton>
              ) : (
                <Button type="submit" disabled={!hasAcceptedTerms}>
                  See my FundReady™ report
                </Button>
              )}
            </div>
          </form>
        </FormProvider>

        {/* 400s are fully reported under the email field; this line is only
            for unexpected failures (network, 5xx). */}
        {submitMutation.isError && submitMutation.error.status !== 400 && (
          <p role="alert" className="text-fr-negative text-[13.5px]">
            Something went wrong. Please try again.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
